import React from 'react';
import PageLayout from '../components/PageLayout';
import SEOHead from '../components/SEOHead';

const cloudServices = [
  {
    icon: '/pictures/agile_development_process_11fe77acf4.svg',
    title: 'Cloud Migration',
    desc: 'Seamless migration of your on-premises workloads to AWS, Azure, or Google Cloud. We minimize downtime and risk with proven migration frameworks and automation tools.',
  },
  {
    icon: '/pictures/stack_248277a23c.svg',
    title: 'Infrastructure as Code',
    desc: 'Manage and provision cloud infrastructure through Terraform and Ansible. Version-controlled, repeatable, and auditable infrastructure that scales with your team.',
  },
  {
    icon: '/pictures/faster_cycles_cb82a46bc5.svg',
    title: 'Container & Kubernetes',
    desc: 'Design and operate Docker and Kubernetes environments. From cluster setup to workload orchestration, we build resilient, auto-scaling container platforms.',
  },
  {
    icon: '/pictures/shield_check_7246d46e1b.svg',
    title: 'Cloud Security',
    desc: 'Harden your cloud environment with security groups, IAM policies, encryption, and continuous compliance monitoring across multi-cloud deployments.',
  },
  {
    icon: '/pictures/support_a1eed40502.svg',
    title: 'Managed Cloud Operations',
    desc: '24/7 monitoring, incident response, cost optimization, and capacity planning for your cloud infrastructure. So your team can focus on building, not firefighting.',
  },
  {
    icon: '/pictures/reduced_downtime_c019ff5c72.svg',
    title: 'Disaster Recovery',
    desc: 'Design and test robust DR plans with RPO/RTO objectives. Multi-region backups, failover automation, and regular recovery drills to keep your business resilient.',
  },
];

const providers = [
  { name: 'Amazon Web Services', abbr: 'AWS', color: '#FF9900' },
  { name: 'Microsoft Azure', abbr: 'Azure', color: '#0078D4' },
  { name: 'Google Cloud', abbr: 'GCP', color: '#4285F4' },
  { name: 'DigitalOcean', abbr: 'DO', color: '#0080FF' },
];

export default function CloudInfrastructure() {
  return (
    <PageLayout>
      <SEOHead
        title="Cloud Infrastructure Services"
        description="Design, deployment, and management of scalable cloud solutions on AWS, Azure, and Google Cloud. Cloud migration, Kubernetes, IaC, and 24/7 managed operations."
        keywords="cloud infrastructure, AWS, Azure, Google Cloud, cloud migration, Kubernetes, Docker, infrastructure as code, Terraform, cloud security"
        url="/services/cloud-infrastructure"
      />
      {/* Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Services</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>Cloud Infrastructure</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            Cloud Infrastructure
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            Design, deployment, and management of scalable cloud solutions on AWS, Azure, and Google Cloud — built for performance, security, and cost efficiency.
          </p>
        </div>
      </div>

      {/* Intro */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>Build on a Cloud Foundation That Scales</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8, marginBottom: '20px' }}>
              Modern businesses run on cloud infrastructure. Whether you\'re lifting and shifting existing workloads, building cloud-native applications, or optimizing a multi-cloud environment, DISION Tech has the expertise to guide every step.
            </p>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>
              We combine deep cloud engineering skills with security-first principles and DevNetOps practices to deliver infrastructure that is scalable, resilient, and cost-optimized from day one.
            </p>

            {/* Cloud providers */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px', flexWrap: 'wrap' }}>
              {providers.map((p, i) => (
                <div key={i} style={{ padding: '8px 18px', borderRadius: '20px', border: '2px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: p.color }} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{p.abbr}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/how_it_works_73f6b0e32b.png" alt="Cloud Infrastructure" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Services */}
      <section style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Cloud Services We Offer</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '500px' }}>End-to-end cloud capabilities from strategy to operations.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {cloudServices.map((s, i) => (
              <div key={i} className="service-card" style={{ background: 'white', borderRadius: '16px', padding: '28px', border: '1px solid #E5E7EB' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', padding: '11px' }}>
                  <img src={s.icon} alt={s.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>{s.title}</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why DISION */}
      <section style={{ padding: '80px 24px', background: '#111827' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: '700', color: 'white', marginBottom: '20px', letterSpacing: '-0.5px', lineHeight: 1.3 }}>
            Ready to modernize your infrastructure?
          </h2>
          <p style={{ fontSize: '17px', color: '#9CA3AF', marginBottom: '36px', lineHeight: 1.7 }}>
            Start your cloud journey with DISION Tech. Whether you\'re just beginning or optimizing an existing environment, we partner with you at every stage.
          </p>
          <button
            style={{ backgroundColor: '#F97316', color: 'white', padding: '14px 36px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '16px', fontWeight: '600', transition: 'background-color 0.2s ease' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#EA580C'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F97316'}
          >
            Contact us
          </button>
        </div>
      </section>
    </PageLayout>
  );
}
